// Boardroom: the governance screen (design §3/§11 M2-lite). Board confidence,
// seats and consequences; the term sheet on the table (accept/reject — the
// game's Stage-2 decision); the cap table with full round history.

import type { ScreenProps } from "./shared.js";
import { money, monthLabel, pct } from "../format.js";
import { Bar } from "../components/widgets.js";
import { totalShares } from "../../engine/systems/finance.js";

export function Boardroom({ snap, content, session }: ScreenProps) {
  const { view, pending } = snap;
  const fin = view.finance;
  const board = view.board;
  const cfg = content.economy.board;
  const total = totalShares(fin);

  const sheet = fin.openTermSheet;
  const answer = pending.find((c) => c.type === "acceptTermSheet" || c.type === "rejectTermSheet");
  // mirror closeRound's dilution math for the preview (public formula)
  const estShareAfter = (() => {
    if (!sheet) return 0;
    const investorNew = sheet.amount / (sheet.preMoney / total);
    const poolAdd = Math.max(0, (sheet.poolTopUpPct * (total + investorNew) - fin.poolShares) / (1 - sheet.poolTopUpPct));
    return fin.founderShares / (total + investorNew + poolAdd);
  })();

  const mood =
    board.confidence >= cfg.concernFloor + 20
      ? "The room is with you."
      : board.confidence >= cfg.concernFloor
        ? "Polite, but the questions are getting shorter."
        : board.confidence >= cfg.ultimatumFloor
          ? "Worse terms on the next round — deliver something."
          : "One more bad quarter and the vote is about you.";

  return (
    <div>
      <h2>BOARDROOM</h2>

      <div className="row">
        <div className="tile">
          <div className="k">BOARD CONFIDENCE</div>
          <div className={`v${board.confidence < cfg.ultimatumFloor ? " bad" : board.confidence < cfg.concernFloor ? " warn" : ""}`}>
            {board.confidence.toFixed(0)}/100
          </div>
          <Bar frac={board.confidence / 100} mark={cfg.concernFloor / 100} amber={board.confidence < cfg.concernFloor} />
          <div className="sub">{mood}</div>
        </div>
        <div className="tile">
          <div className="k">BOARD SEATS</div>
          <div className="v">
            {board.investorSeats} investor{board.investorSeats === 1 ? "" : "s"}
          </div>
          <div className="sub">
            {board.investorSeats >= cfg.ousterMinInvestorSeats
              ? "investors can outvote you — confidence is survival"
              : "you still control the board"}
          </div>
        </div>
        <div className="tile">
          <div className="k">LAST MEETING</div>
          <div className="v">{board.lastMeetingMonth !== null ? monthLabel(board.lastMeetingMonth) : "—"}</div>
          <div className="sub">quarterly ritual{view.stage === "angel" ? " (starts at VC stage)" : ""}</div>
        </div>
      </div>

      <div className="panel">
        <div className="title">STAKEHOLDERS — every meter is somebody's memory (design §11)</div>
        <Bar frac={board.confidence / 100} label="the board" right={`${board.confidence.toFixed(0)}/100`} amber={board.confidence < cfg.concernFloor} />
        <Bar
          frac={view.stakeholders.foundryRel / 100}
          label="the foundry (PARTNERS screen)"
          right={`${view.stakeholders.foundryRel.toFixed(0)}/100`}
          amber={view.stakeholders.foundryRel < content.economy.stakeholders.penaltyRelCeil}
        />
        <Bar
          frac={view.talent.morale / 100}
          label="the employees (PEOPLE screen)"
          right={`${view.talent.morale.toFixed(0)}/100`}
          amber={view.talent.morale < 45}
        />
        {Object.entries(view.stakeholders.customers)
          .filter(([id]) => view.products.some((p) => p.marketId === id && p.active))
          .map(([id, rel]) => (
            <Bar
              key={id}
              frac={rel / 100}
              label={`customers — ${content.markets.find((mk) => mk.id === id)?.displayName ?? id}`}
              right={`${rel.toFixed(0)}/100`}
              amber={rel < 45}
            />
          ))}
      </div>

      {board.hiringFreeze && (
        <div className="panel">
          <div className="title">⚠ HIRING FREEZE</div>
          <span className="bad-text">
            The board has frozen hiring until confidence recovers past {cfg.freezeLiftFloor}. Ship something, close money, or shrink the burn.
          </span>
        </div>
      )}

      {sheet && (
        <div className="panel">
          <div className="title">TERM SHEET ON THE TABLE — expires in {sheet.monthsLeft} month{sheet.monthsLeft === 1 ? "" : "s"}</div>
          <div className="scroll-x">
            <table>
              <tbody>
                <tr>
                  <td>round</td>
                  <td className="r">
                    <b>{["Seed", "Series A", "Series B", "Series C", "Series D"][sheet.round - 1] ?? `Round ${sheet.round}`}</b>
                  </td>
                </tr>
                <tr>
                  <td>investment</td>
                  <td className="r">
                    <b>{money(sheet.amount)}</b>
                  </td>
                </tr>
                <tr>
                  <td>pre-money valuation</td>
                  <td className="r">
                    {money(sheet.preMoney)}
                    {sheet.downRound && <span className="bad-text"> — DOWN ROUND</span>}
                  </td>
                </tr>
                <tr>
                  <td>option-pool top-up (pre-money)</td>
                  <td className="r">{pct(sheet.poolTopUpPct, 0)} of post-round</td>
                </tr>
                <tr>
                  <td>board seat</td>
                  <td className="r">{sheet.boardSeat ? "yes — lead takes a seat" : "no"}</td>
                </tr>
                <tr>
                  <td>your share after close (est.)</td>
                  <td className="r">
                    {pct(estShareAfter, 1)} (now {pct(fin.founderShare, 1)})
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
            <button
              className={`act warn${answer?.type === "acceptTermSheet" ? " queued" : ""}`}
              onClick={() => session.queue({ type: "acceptTermSheet" })}
            >
              {answer?.type === "acceptTermSheet" ? "ACCEPT QUEUED" : "ACCEPT — TAKE THE MONEY"}
            </button>
            <button
              className={`act bad${answer?.type === "rejectTermSheet" ? " queued" : ""}`}
              onClick={() => session.queue({ type: "rejectTermSheet" })}
            >
              {answer?.type === "rejectTermSheet" ? "REJECT QUEUED" : "REJECT — KEEP LOOKING"}
            </button>
            <span className="hint">
              rejecting keeps the raise alive (maybe a better lead appears); the search is withdrawn after{" "}
              {content.economy.financing.maxMonths} months total
            </span>
          </div>
          {sheet.downRound && (
            <div className="hint" style={{ marginTop: 6 }}>
              A down round prices the company below the last post-money ({money(fin.lastPostMoney)}). Accepting one costs the board's
              confidence — but dying costs more.
            </div>
          )}
        </div>
      )}

      <div className="panel">
        <div className="title">CAP TABLE</div>
        <div className="scroll-x">
          <table>
            <thead>
              <tr>
                <th>holder</th>
                <th className="r">shares</th>
                <th className="r">fully diluted</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>founders</td>
                <td className="r">{fin.founderShares.toLocaleString("en-US")}</td>
                <td className="r">
                  <b>{pct(fin.founderShares / total, 1)}</b>
                </td>
              </tr>
              <tr>
                <td>investors (preferred)</td>
                <td className="r">{fin.investorShares.toLocaleString("en-US")}</td>
                <td className="r">{pct(fin.investorShares / total, 1)}</td>
              </tr>
              <tr>
                <td>option pool</td>
                <td className="r">{fin.poolShares.toLocaleString("en-US")}</td>
                <td className="r">{pct(fin.poolShares / total, 1)}</td>
              </tr>
              <tr>
                <td>public float</td>
                <td className="r">{fin.publicShares.toLocaleString("en-US")}</td>
                <td className="r">{pct(fin.publicShares / total, 1)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {fin.rounds.length > 0 && (
        <div className="panel">
          <div className="title">ROUND HISTORY</div>
          <div className="scroll-x">
            <table>
              <thead>
                <tr>
                  <th>round</th>
                  <th>closed</th>
                  <th className="r">raised</th>
                  <th className="r">pre-money</th>
                  <th className="r">post-money</th>
                  <th>terms</th>
                </tr>
              </thead>
              <tbody>
                {fin.rounds.map((r) => (
                  <tr key={r.name + r.month}>
                    <td>{r.name}</td>
                    <td>{monthLabel(r.month)}</td>
                    <td className="r">{money(r.amount)}</td>
                    <td className="r">{money(r.preMoney)}</td>
                    <td className="r">{money(r.postMoney)}</td>
                    <td>
                      {r.boardSeat ? "board seat" : "no seat"}
                      {r.poolSharesAdded > 0 ? " · pool top-up" : ""}
                      {r.downRound ? <span className="bad-text"> · DOWN ROUND</span> : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="panel">
        <div className="title">HOW THE BOARD SCORES YOU</div>
        <span className="hint">
          Confidence drifts toward {cfg.baseline} and moves on events: silicon results, product releases, closed and failed rounds
          (down rounds hurt badly), field failures, earnings calls, pulled IPOs. Below {cfg.concernFloor}: financing terms degrade. Below{" "}
          {cfg.ultimatumFloor}: hiring freeze. At the floor with {cfg.ousterMinInvestorSeats}+ investor seats: they vote you out and the
          campaign ends. Perks above {content.economy.termSheets.confHighFloor}: better term sheets.
        </span>
      </div>
    </div>
  );
}
